import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { ChevronLeft, Play, Pause, RefreshCw, Square, CheckCheck, CheckCircle2, Info } from "lucide-react";

const PRESETS = [
  { key: "classic", focusMin: 25, breakMin: 5, labelKey: "focus_timer_preset_classic" },
  { key: "sprint", focusMin: 15, breakMin: 3, labelKey: "focus_timer_preset_sprint" },
  { key: "extended", focusMin: 50, breakMin: 10, labelKey: "focus_timer_preset_extended" },
];
const BREATHING_SECONDS = 120;
const RING_SIZE = 240;
const RING_STROKE = 14;
const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;

function formatMMSS(sec) {
  const m = Math.floor(Math.max(0, sec) / 60);
  const s = Math.max(0, sec) % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const PHASE_COLOR = { focus: "#575787", breathing: "#5E7A73", break: "#B87A3E", done: "hsl(var(--primary))", idle: "#575787" };

export default function FocusTimer() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("focus_timer_title"));

  const [preset, setPreset] = useState(PRESETS[0]);
  const [phase, setPhase] = useState("idle");
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(preset.focusMin * 60);
  const [totalForPhase, setTotalForPhase] = useState(preset.focusMin * 60);
  const [completed, setCompleted] = useState(0);

  useEffect(() => {
    if (phase === "idle") { setRemaining(preset.focusMin * 60); setTotalForPhase(preset.focusMin * 60); }
  }, [preset, phase]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setRemaining((r) => (r <= 1 ? 0 : r - 1)), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (!running || remaining > 0) return;
    if (phase === "focus") { setCompleted((c) => c + 1); setPhase("breathing"); setRemaining(BREATHING_SECONDS); setTotalForPhase(BREATHING_SECONDS); }
    else if (phase === "breathing") { setPhase("break"); setRemaining(preset.breakMin * 60); setTotalForPhase(preset.breakMin * 60); }
    else if (phase === "break") { setPhase("done"); setRunning(false); setRemaining(0); }
  }, [remaining, running, phase, preset.breakMin]);

  const startFresh = useCallback(() => { setPhase("focus"); setRemaining(preset.focusMin * 60); setTotalForPhase(preset.focusMin * 60); setRunning(true); }, [preset]);
  const togglePause = useCallback(() => setRunning((r) => !r), []);
  const reset = useCallback(() => { setPhase("idle"); setRunning(false); setCompleted(0); setRemaining(preset.focusMin * 60); setTotalForPhase(preset.focusMin * 60); }, [preset]);
  const startNextCycle = useCallback(() => { setPhase("focus"); setRemaining(preset.focusMin * 60); setTotalForPhase(preset.focusMin * 60); setRunning(true); }, [preset]);

  const progress = totalForPhase > 0 ? (totalForPhase - remaining) / totalForPhase : 0;
  const phaseColor = PHASE_COLOR[phase] || PHASE_COLOR.idle;
  const phaseLabelKey = { focus: "focus_timer_phase_focus", breathing: "focus_timer_phase_breathing", break: "focus_timer_phase_break", done: "focus_timer_phase_done", idle: "focus_timer_phase_ready" }[phase];
  const hint = phase === "breathing" ? t("focus_timer_hint_breathing") : phase === "break" ? t("focus_timer_hint_break") : phase === "focus" ? t("focus_timer_hint_focus") : phase === "done" ? t("focus_timer_hint_done", { n: completed }) : t("focus_timer_hint_ready");

  return (
    <div className="mx-auto max-w-lg" data-testid="focus-timer-screen">
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="focus-timer-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-xl text-foreground">{t("focus_timer_title")}</h1>
      </div>

      <div className="flex flex-col items-center gap-6">
        <div className="relative" style={{ width: RING_SIZE, height: RING_SIZE }}>
          <svg width={RING_SIZE} height={RING_SIZE}>
            <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R} stroke="hsl(var(--secondary))" strokeWidth={RING_STROKE} fill="none" />
            <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R} stroke={phaseColor} strokeWidth={RING_STROKE} strokeLinecap="round" fill="none"
              strokeDasharray={`${RING_C} ${RING_C}`} strokeDashoffset={RING_C - progress * RING_C}
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`} style={{ transition: "stroke-dashoffset 1s linear" }} />
          </svg>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-center">
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: phaseColor }}>{t(phaseLabelKey)}</span>
            <span data-testid="focus-timer-countdown" className="font-display text-5xl tabular-nums text-foreground">{phase === "idle" ? formatMMSS(preset.focusMin * 60) : formatMMSS(remaining)}</span>
            <span className="max-w-[12rem] text-xs leading-snug text-muted-foreground">{hint}</span>
          </div>
        </div>

        {phase === "breathing" && running ? <BoxBreathPacer /> : null}

        {phase === "idle" ? (
          <div className="flex flex-wrap justify-center gap-2">
            {PRESETS.map((p) => {
              const active = preset.key === p.key;
              return (
                <button key={p.key} data-testid={`focus-timer-preset-${p.key}`} onClick={() => setPreset(p)}
                  className={`flex min-w-[88px] flex-col items-center rounded-full border px-4 py-2.5 ${active ? "text-white" : "border-border bg-card text-foreground"}`}
                  style={active ? { backgroundColor: phaseColor, borderColor: phaseColor } : undefined}>
                  <span className="font-display text-sm">{t(p.labelKey)}</span>
                  <span className={`text-[11px] ${active ? "text-white/85" : "text-muted-foreground"}`}>{p.focusMin} · {p.breakMin} min</span>
                </button>
              );
            })}
          </div>
        ) : null}

        {completed > 0 || phase !== "idle" ? (
          <div data-testid="focus-timer-counter" className="inline-flex items-center gap-1.5 rounded-full bg-secondary/60 px-3 py-1.5 text-xs font-medium text-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> {t("focus_timer_completed_sprints", { n: completed })}
          </div>
        ) : null}

        <div className="flex items-center justify-center gap-3">
          {phase === "idle" ? (
            <button data-testid="focus-timer-start" onClick={startFresh} className="flex min-w-[140px] items-center justify-center gap-2 rounded-full px-5 py-3 font-display text-white" style={{ backgroundColor: phaseColor }}>
              <Play className="h-4 w-4" /> {t("focus_timer_start")}
            </button>
          ) : phase === "done" ? (
            <>
              <button data-testid="focus-timer-next" onClick={startNextCycle} className="flex min-w-[140px] items-center justify-center gap-2 rounded-full px-5 py-3 font-display text-white" style={{ backgroundColor: phaseColor }}>
                <RefreshCw className="h-4 w-4" /> {t("focus_timer_start_next")}
              </button>
              <button data-testid="focus-timer-finish" onClick={() => navigate(-1)} className="flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-3 text-sm text-foreground">
                <CheckCheck className="h-4 w-4" /> {t("focus_timer_finish")}
              </button>
            </>
          ) : (
            <>
              <button data-testid="focus-timer-toggle" onClick={togglePause} className="flex min-w-[140px] items-center justify-center gap-2 rounded-full px-5 py-3 font-display text-white" style={{ backgroundColor: phaseColor }}>
                {running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />} {running ? t("focus_timer_pause") : t("focus_timer_resume")}
              </button>
              <button data-testid="focus-timer-reset" onClick={reset} className="flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-3 text-sm text-foreground">
                <Square className="h-4 w-4" /> {t("focus_timer_reset")}
              </button>
            </>
          )}
        </div>

        <div className="flex max-w-sm items-start gap-2 rounded-xl bg-primary/10 p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-xs leading-relaxed text-foreground">{t("focus_timer_tip")}</p>
        </div>
      </div>
    </div>
  );
}

function BoxBreathPacer() {
  const { t } = useI18n();
  const [label, setLabel] = useState("inhale");
  const [scale, setScale] = useState(1);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    async function run() {
      while (!cancelled.current) {
        setLabel("inhale"); setScale(1.6); await sleep(4000); if (cancelled.current) return;
        setLabel("hold_in"); await sleep(4000); if (cancelled.current) return;
        setLabel("exhale"); setScale(1); await sleep(4000); if (cancelled.current) return;
        setLabel("hold_out"); await sleep(4000);
      }
    }
    run();
    return () => { cancelled.current = true; };
  }, []);

  const labelKey = label === "inhale" ? "breath_inhale" : label === "exhale" ? "breath_exhale" : label === "hold_in" ? "breath_hold_in" : "breath_hold_out";

  return (
    <div className="flex h-28 flex-col items-center justify-center gap-2" data-testid="focus-timer-pacer">
      <div className="h-16 w-16 rounded-full border-2" style={{ backgroundColor: "#5E7A7333", borderColor: "#5E7A73", transform: `scale(${scale})`, transition: "transform 4s ease-in-out" }} />
      <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#5E7A73" }}>{t(labelKey)}</span>
    </div>
  );
}
